import * as THREE from 'three';
import { materialLibrary as M } from '../assets/MaterialLibrary';
import { SIGNAL } from '../assets/MaterialLibrary';

// Course actors: checkpoint gates (ordered), boost pods, buoys.

export class Gate {
  readonly group = new THREE.Group();
  readonly position = new THREE.Vector2();
  readonly radius: number;
  readonly heading: number;
  passed = false;

  constructor(index: number, x: number, z: number, radius: number, heading: number) {
    this.position.set(x, z);
    this.radius = radius;
    this.heading = heading;

    // Two pylons with a light bridge; next gate glows objective green.
    const activeMat = new THREE.MeshStandardMaterial({
      color: '#0a2416', emissive: SIGNAL.objective, emissiveIntensity: 2.8, roughness: 0.4,
    });
    const idleMat = new THREE.MeshStandardMaterial({
      color: '#123a28', emissive: SIGNAL.objective, emissiveIntensity: 0.9, roughness: 0.4,
    });
    this.activeMaterial = activeMat;
    this.idleMaterial = idleMat;

    for (const side of [-1, 1]) {
      const pylon = new THREE.Mesh(
        new THREE.CylinderGeometry(0.26, 0.4, 4.6, 10),
        idleMat,
      );
      pylon.position.set(side * radius, 1.6, 0);
      pylon.castShadow = true;
      this.group.add(pylon);
      const beacon = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 10, 10),
        new THREE.MeshStandardMaterial({
          color: '#06140d', emissive: SIGNAL.objective, emissiveIntensity: 3.2, roughness: 0.4,
        }),
      );
      beacon.position.set(side * radius, 4.2, 0);
      this.group.add(beacon);
    }

    // Top beam
    const beam = new THREE.Mesh(
      new THREE.BoxGeometry(radius * 2, 0.28, 0.24),
      idleMat,
    );
    beam.position.set(0, 2.9, 0);
    this.group.add(beam);

    // Number plate
    const plateCanvas = document.createElement('canvas');
    plateCanvas.width = 128;
    plateCanvas.height = 128;
    const ctx = plateCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#06140d';
      ctx.fillRect(0, 0, 128, 128);
      ctx.fillStyle = SIGNAL.objective;
      ctx.font = 'bold 84px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(index + 1), 64, 70);
    }
    const plateTex = new THREE.CanvasTexture(plateCanvas);
    plateTex.colorSpace = THREE.SRGBColorSpace;
    const plate = new THREE.Mesh(
      new THREE.PlaneGeometry(0.9, 0.9),
      new THREE.MeshBasicMaterial({ map: plateTex, transparent: true }),
    );
    plate.position.set(0, 3.75, 0);
    this.group.add(plate);
    this.plate = plate;

    // Checkered pattern strip on beam
    const flagCanvas = document.createElement('canvas');
    flagCanvas.width = 64;
    flagCanvas.height = 16;
    const fctx = flagCanvas.getContext('2d');
    if (fctx) {
      for (let cx = 0; cx < 8; cx++) {
        for (let cy = 0; cy < 2; cy++) {
          fctx.fillStyle = (cx + cy) % 2 === 0 ? '#101418' : '#f4f6f8';
          fctx.fillRect(cx * 8, cy * 8, 8, 8);
        }
      }
    }
    const flagTex = new THREE.CanvasTexture(flagCanvas);
    flagTex.colorSpace = THREE.SRGBColorSpace;
    const flag = new THREE.Mesh(
      new THREE.PlaneGeometry(radius * 2, 0.42),
      new THREE.MeshBasicMaterial({ map: flagTex, side: THREE.DoubleSide, transparent: true }),
    );
    flag.position.set(0, 2.5, 0);
    this.group.add(flag);
    this.flag = flag;

    this.group.position.set(x, 0, z);
    this.group.rotation.y = heading;
  }

  private readonly activeMaterial: THREE.MeshStandardMaterial;
  private readonly idleMaterial: THREE.MeshStandardMaterial;
  private readonly plate: THREE.Mesh;
  private readonly flag: THREE.Mesh;

  update(delta: number, elapsed: number, isActive: boolean): void {
    const mat = isActive ? this.activeMaterial : this.idleMaterial;
    for (const child of this.group.children) {
      const mesh = child as THREE.Mesh;
      if (mesh.material === this.idleMaterial || mesh.material === this.activeMaterial) {
        mesh.material = mat;
      }
    }
    if (isActive) {
      this.plate.position.y = 3.75 + Math.sin(elapsed * 3) * 0.1;
      this.flag.rotation.y = Math.sin(elapsed * 2.4) * 0.08;
    }
    void delta;
  }

  markPassed(): void {
    this.passed = true;
  }

  reset(): void {
    this.passed = false;
  }
}

export class BoostPod {
  readonly group = new THREE.Group();
  readonly position = new THREE.Vector2();
  active = true;
  readonly index: number;

  constructor(index: number, x: number, z: number) {
    this.index = index;
    this.position.set(x, z);
    this.group.position.set(x, 0.9, z);

    // Faceted nitro capsule: glass shell + inner core + ring
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.3, 0), M.boost);
    this.group.add(core);
    const shell = new THREE.Mesh(new THREE.IcosahedronGeometry(0.52, 1), M.glass);
    this.group.add(shell);
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.7, 0.04, 8, 28),
      new THREE.MeshBasicMaterial({ color: SIGNAL.boost, transparent: true, opacity: 0.75 }),
    );
    ring.rotation.x = Math.PI / 2;
    this.group.add(ring);
    this.ring = ring;
  }

  private readonly ring: THREE.Mesh;

  update(delta: number, elapsed: number): void {
    if (!this.active) return;
    this.group.rotation.y += delta * 1.6;
    this.ring.rotation.z += delta * 2.2;
    this.group.position.y = 0.9 + Math.sin(elapsed * 2.2 + this.index * 1.7) * 0.18;
  }

  collect(): void {
    this.active = false;
    this.group.visible = false;
  }

  reset(): void {
    this.active = true;
    this.group.visible = true;
  }
}

export class BuoyField {
  readonly groups: THREE.Group[] = [];
  readonly positions: THREE.Vector2[] = [];

  add(x: number, z: number, builder: () => THREE.Group): void {
    const g = builder();
    g.position.set(x, 0, z);
    this.groups.push(g);
    this.positions.push(new THREE.Vector2(x, z));
  }

  update(elapsed: number): void {
    for (let i = 0; i < this.groups.length; i++) {
      const g = this.groups[i];
      const bob = Math.sin(elapsed * 1.4 + i * 1.3) * 0.09;
      g.position.y = bob;
      g.rotation.z = Math.sin(elapsed * 1.1 + i) * 0.06;
      g.rotation.x = Math.cos(elapsed * 0.9 + i * 2.1) * 0.05;
    }
  }
}
